import { useParams, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, MapPin, Check, X, Phone, Mail, AlertTriangle, Calculator, TrendingDown } from 'lucide-react';
import { seoCities, seoServices } from '@/config/seoConfig';
import { FiscalBanner } from '@/components/FiscalBanner';
import { FiscalCountdown } from '@/components/FiscalCountdown';
import { FiscalSimulator } from '@/components/FiscalSimulator';
import { useRef } from 'react';

// Find which canton a city belongs to
const findCanton = (city) => {
  for (const [canton, cities] of Object.entries(seoCities)) {
    if (cities.some(c => c.toLowerCase() === city.toLowerCase())) {
      return canton;
    }
  }
  return 'vaud';
};

// Get nearby cities for internal linking
const getNearbyCities = (currentCity, canton) => {
  const cities = seoCities[canton] || [];
  return cities.filter(c => c.toLowerCase() !== currentCity.toLowerCase()).slice(0, 6);
};

export const SEOCityPage = ({ service: serviceProp }) => {
  const { city } = useParams();
  const service = serviceProp;
  const formRef = useRef(null);
  const simulatorRef = useRef(null);
  
  // Decode and format city name
  const cityName = decodeURIComponent(city || '').replace(/-/g, ' ');
  const formattedCity = cityName.charAt(0).toUpperCase() + cityName.slice(1);
  
  // Check if this is a renovation page
  const isRenovation = service === 'renovation';
  
  // Find service config
  const serviceConfig = seoServices.find(s => s.slug === service) || seoServices[0];
  
  // Find canton and nearby cities
  const canton = findCanton(formattedCity);
  const cantonName = canton.charAt(0).toUpperCase() + canton.slice(1);
  const nearbyCities = getNearbyCities(formattedCity, canton);
  
  // Generate dynamic content (no-keyword technique - semantic SEO)
  const pageTitle = serviceConfig.title.replace('{city}', formattedCity);
  const pageDescription = serviceConfig.description.replace('{city}', formattedCity);
  const h1 = isRenovation 
    ? `Rénovation à ${formattedCity} : profitez des déductions fiscales avant qu'il ne soit trop tard`
    : serviceConfig.h1.replace('{city}', formattedCity);
  const intro = isRenovation
    ? `Suite à la réforme votée en Suisse, les déductions fiscales sur les rénovations vont disparaître. À ${formattedCity}, vous pouvez encore optimiser vos travaux. Demain, ce sera 100% à votre charge.`
    : serviceConfig.intro.replace('{city}', formattedCity);

  // Structured data for local service
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${serviceConfig.name} à ${formattedCity}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Homely Solutions",
      "telephone": "+41-79-123-45-67"
    },
    "areaServed": {
      "@type": "City",
      "name": formattedCity,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": `Canton de ${cantonName}`
      }
    },
    "description": pageDescription
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSimulator = () => {
    simulatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Questions for the form
  const getQuestions = () => {
    switch(service) {
      case 'nettoyage':
        return [
          { id: 'service', label: 'Type de nettoyage', type: 'select', options: ['Canapé', 'Terrasse', 'Vitres', 'Fin de chantier', 'Après bail', 'Autre'] },
          { id: 'surface', label: 'Surface approximative', type: 'text', placeholder: 'Ex: 80 m²' }
        ];
      case 'renovation':
        return [
          { id: 'type_reno', label: 'Type de rénovation', type: 'select', options: ['Complète', 'Cuisine', 'Salle de bain', 'Peinture', 'Autre'] },
          { id: 'surface', label: 'Surface', type: 'text', placeholder: 'Ex: 100 m²' },
          { id: 'budget', label: 'Budget estimatif', type: 'text', placeholder: 'Ex: 25\'000 CHF' },
          { id: 'optimisation_fiscale', label: 'Optimisation fiscale ?', type: 'select', options: ['Oui, c\'est prioritaire', 'J\'aimerais en savoir plus', 'Non'] }
        ];
      case 'montage':
        return [
          { id: 'type', label: 'Service souhaité', type: 'select', options: ['Montage meubles', 'Transport/Livraison', 'Cuisine', 'Handy Man'] },
          { id: 'volume', label: 'Volume estimé', type: 'select', options: ['Petit', 'Moyen', 'Grand'] }
        ];
      case 'conciergerie':
        return [
          { id: 'bien_type', label: 'Type de bien', type: 'select', options: ['Appartement', 'Chalet', 'Villa', 'Studio', 'Maison'] },
          { id: 'chambres', label: 'Nombre de chambres', type: 'select', options: ['Studio', '1 chambre', '2 chambres', '3 chambres', '4+ chambres'] },
          { id: 'deja_loue', label: 'Déjà en location ?', type: 'select', options: ['Oui, je veux optimiser', 'Non, je veux commencer', 'Je réfléchis'] }
        ];
      case 'immo':
        return [
          { id: 'type_bien', label: 'Type de bien', type: 'select', options: ['Appartement', 'Maison', 'Chalet', 'Villa', 'Terrain'] },
          { id: 'surface', label: 'Surface', type: 'text', placeholder: 'Ex: 120 m²' },
          { id: 'deja_vente', label: 'Déjà en vente ?', type: 'select', options: ['Oui', 'Non', 'Je réfléchis'] }
        ];
      case 'marketing':
        return [
          { id: 'besoin', label: 'Besoin principal', type: 'select', options: ['Site web', 'SEO', 'Publicité', 'Réseaux sociaux', 'Pack complet'] },
          { id: 'secteur', label: 'Secteur d\'activité', type: 'text', placeholder: 'Ex: Immobilier, commerce...' }
        ];
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title={`${pageTitle} | Homely Solutions`}
        description={pageDescription}
        keywords={`${serviceConfig.name} ${formattedCity}, ${serviceConfig.slug} ${cantonName}, Homely Solutions ${formattedCity}`}
        canonical={`https://www.homely-solutions.ch/${service}/${city}`}
        jsonLd={localServiceSchema}
      />
      
      {/* Bannière fiscale uniquement pour rénovation */}
      {isRenovation && <FiscalBanner onSimulatorClick={scrollToSimulator} />}
      
      <Header />
      
      {/* Hero Section - Adapté pour rénovation */}
      <section 
        className="py-10 sm:py-16 md:py-20" 
        style={{ 
          backgroundColor: isRenovation ? 'var(--anthracite)' : 'var(--sage)', 
          color: 'white' 
        }}
      >
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
            <MapPin className="h-5 w-5 sm:h-6 sm:w-6" />
            <Badge className={`text-xs sm:text-sm ${isRenovation ? "bg-orange-500 text-white border-orange-400" : "bg-white/20 text-white border-white/30"}`}>
              {formattedCity}, Canton de {cantonName}
            </Badge>
            {isRenovation && (
              <Badge className="bg-red-500 text-white border-red-400 text-xs sm:text-sm">
                Fenêtre fiscale limitée
              </Badge>
            )}
          </div>
          
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            {h1}
          </h1>
          
          <p className="text-sm sm:text-base md:text-lg text-white/90 mb-5 sm:mb-8 max-w-3xl">
            {intro}
          </p>

          {/* Compte à rebours pour rénovation */}
          {isRenovation && (
            <div className="mb-5 sm:mb-8 inline-block">
              <FiscalCountdown />
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {isRenovation ? (
              <>
                <Button 
                  size="lg" 
                  className="h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full bg-orange-500 hover:bg-orange-600 w-full sm:w-auto"
                  onClick={scrollToSimulator}
                >
                  <Calculator className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Calculer mes économies
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)] w-full sm:w-auto"
                  onClick={scrollToForm}
                >
                  <TrendingDown className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Voir combien je perds
                </Button>
              </>
            ) : (
              <Button 
                size="lg" 
                className="btn-premium h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full w-full sm:w-auto"
                onClick={scrollToForm}
              >
                Demander un devis gratuit
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Section Problème - Uniquement rénovation */}
      {isRenovation && (
        <section className="py-10 sm:py-12 md:py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 sm:mb-10">
              <Badge className="mb-3 sm:mb-4 bg-red-100 text-red-700 text-xs sm:text-sm">Le constat à {formattedCity}</Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 px-2" style={{ fontFamily: 'var(--font-heading)' }}>
                Attendre vos travaux à {formattedCity} vous coûte déjà de l'argent.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
              {/* Aujourd'hui */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-green-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                  Aujourd'hui
                </h3>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    'Travaux partiellement déductibles',
                    'Réduction d\'impôts',
                    'Coût réel optimisé'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-green-900">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Demain */}
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  Après réforme
                </h3>
                <ul className="space-y-3">
                  {[
                    'Plus de déduction fiscale',
                    'Même travaux, coût plus élevé',
                    'Aucune optimisation'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <X className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-red-900">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-center mt-8 p-4 bg-orange-100 rounded-xl max-w-xl mx-auto">
              <p className="text-lg font-bold text-orange-800">
                À {formattedCity}, vous ne perdez pas du temps.<br />
                <span className="text-xl">Vous perdez de l'argent.</span>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Simulateur fiscal - Uniquement rénovation */}
      {isRenovation && (
        <section className="py-16 bg-gray-50" ref={simulatorRef}>
          <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              <div>
                <Badge className="mb-4 bg-orange-100 text-orange-700">Simulation pour {formattedCity}</Badge>
                <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  Combien allez-vous perdre si vous attendez ?
                </h2>
                <p className="text-muted-foreground mb-6">
                  Propriétaire à {formattedCity} ? Faites une estimation rapide de votre économie fiscale potentielle.
                </p>
                <FiscalCountdown />
              </div>
              <FiscalSimulator onCtaClick={scrollToForm} />
            </div>
          </div>
        </section>
      )}

      {/* Impact fiscal par type de travaux - Uniquement rénovation */}
      {isRenovation && (
        <section className="py-16">
          <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                Impact fiscal estimé à {formattedCity}
              </h2>
              <p className="text-muted-foreground">Exemples concrets pour vos projets de rénovation</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { type: 'Salle de bain', budget: 25000 },
                { type: 'Cuisine complète', budget: 40000 },
                { type: 'Rénovation complète', budget: 80000 }
              ].map((item, idx) => {
                const economie = Math.round(item.budget * 0.25);
                return (
                  <div key={idx} className="bg-white rounded-xl shadow-lg p-5 border">
                    <h3 className="font-bold mb-3">{item.type}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Budget</span>
                        <span className="font-semibold">{item.budget.toLocaleString('fr-CH')} CHF</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Économie actuelle</span>
                        <span className="font-semibold">-{economie.toLocaleString('fr-CH')} CHF</span>
                      </div>
                      <div className="flex justify-between text-green-700 font-bold">
                        <span>Coût réel aujourd'hui</span>
                        <span>{(item.budget - economie).toLocaleString('fr-CH')} CHF</span>
                      </div>
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between text-red-600">
                          <span>Après réforme</span>
                          <span className="font-semibold">{item.budget.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-orange-100 rounded text-center">
                      <span className="text-orange-800 font-bold text-sm">
                        Perte : +{economie.toLocaleString('fr-CH')} CHF
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Content Section - Semantic SEO */}
      <section className="py-16">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Main Content */}
            <div className="lg:col-span-2 prose prose-lg max-w-none">
              <h2>Pourquoi choisir Homely Solutions à {formattedCity} ?</h2>
              
              <p>
                Basés en Suisse romande, nous intervenons régulièrement à {formattedCity} et dans tout le canton de {cantonName}. 
                Notre équipe de professionnels qualifiés vous garantit un service irréprochable, 
                dans le respect des standards de qualité suisses les plus exigeants.
              </p>

              {isRenovation && (
                <>
                  <h3>Optimisation fiscale de vos travaux à {formattedCity}</h3>
                  <p>
                    Suite à la réforme votée en Suisse, les déductions fiscales liées aux rénovations vont être supprimées progressivement.
                    À {formattedCity}, vous pouvez encore bénéficier de cet avantage fiscal en réalisant vos travaux maintenant.
                    <strong> Chaque mois d'attente réduit votre avantage.</strong>
                  </p>
                </>
              )}

              <h3>Notre engagement qualité</h3>
              <ul>
                <li><strong>Intervention rapide</strong> – Nous intervenons sous 48 heures à {formattedCity}</li>
                <li><strong>Devis transparent</strong> – Prix fixe, sans mauvaise surprise</li>
                {isRenovation && <li><strong>Conseil fiscal</strong> – Nous vous aidons à optimiser vos déductions</li>}
                <li><strong>Satisfaction garantie</strong> – Nous repassons si nécessaire</li>
                <li><strong>Équipe locale</strong> – Des professionnels qui connaissent {formattedCity}</li>
              </ul>

              <h3>Zones d'intervention depuis {formattedCity}</h3>
              <p>
                Nous couvrons l'ensemble du canton de {cantonName} et ses environs. 
                Que vous soyez au centre de {formattedCity} ou dans les communes avoisinantes, 
                notre équipe se déplace pour répondre à vos besoins.
              </p>

              {/* Internal Links to nearby cities */}
              <h3>{isRenovation ? 'Rénovation dans les villes proches' : 'Nos services dans les villes proches'}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 not-prose">
                {nearbyCities.map((nearbyCity, idx) => (
                  <Link 
                    key={idx}
                    to={`/${service}/${nearbyCity.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm p-2 border rounded hover:bg-[var(--sage)] hover:text-white transition-colors text-center"
                  >
                    {serviceConfig.name} {nearbyCity}
                  </Link>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Other Services */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold mb-4">Nos autres services à {formattedCity}</h3>
                  <div className="space-y-2">
                    {seoServices.filter(s => s.slug !== service).map((s, idx) => (
                      <Link 
                        key={idx}
                        to={`/${s.slug}/${city}`}
                        className="block text-sm p-2 border rounded hover:bg-[var(--sage)] hover:text-white transition-colors"
                      >
                        {s.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Contact Card */}
                <div className="bg-[var(--sage)] text-white rounded-xl p-6">
                  <h3 className="font-semibold mb-4">Contactez-nous</h3>
                  <div className="space-y-3 text-sm">
                    <a href="mailto:contact@homely-solutions.ch" className="flex items-center gap-2 hover:underline">
                      <Mail className="h-4 w-4" />
                      contact@homely-solutions.ch
                    </a>
                    <div className="pt-2 text-white/80">
                      <p>Homely Solutions Sàrl</p>
                      <p>Rue Jean-André Venel 4</p>
                      <p>1400 Yverdon-les-Bains</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16" style={{ backgroundColor: isRenovation ? 'var(--anthracite)' : 'var(--sage)' }} ref={formRef} id="form">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              {isRenovation && <Badge className="mb-4 bg-orange-500">Optimisation fiscale</Badge>}
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                {isRenovation 
                  ? `Profitez des déductions fiscales à ${formattedCity}` 
                  : `Demandez votre devis gratuit à ${formattedCity}`}
              </h2>
              <p className="text-white/80 mb-8 text-lg">
                {isRenovation 
                  ? `Chaque mois d'attente réduit votre avantage fiscal. Faites estimer votre projet à ${formattedCity} et découvrez combien vous pouvez encore économiser.`
                  : `Décrivez votre projet et recevez un devis personnalisé sous 24h pour ${formattedCity} et ses environs.`}
              </p>

              <div className="space-y-3 mb-8">
                {(isRenovation ? [
                  'Estimation de l\'économie fiscale',
                  'Devis détaillé sous 48h',
                  'Accompagnement optimisation fiscale',
                  'Sans engagement'
                ] : [
                  'Réponse sous 24h',
                  'Devis détaillé et transparent',
                  'Intervention rapide',
                  'Satisfaction garantie'
                ]).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full ${isRenovation ? 'bg-orange-500' : 'bg-white/30'} flex items-center justify-center`}>
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <span className="text-white/90 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              {isRenovation && (
                <div className="mb-6">
                  <FiscalCountdown />
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
              {isRenovation && (
                <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800 font-medium">
                    Je veux optimiser mes travaux à {formattedCity} avant la réforme
                  </p>
                </div>
              )}
              <ServiceLeadForm 
                service={service} 
                pole={service} 
                questions={getQuestions()} 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer fiscal - Uniquement rénovation */}
      {isRenovation && (
        <section className="py-4 bg-gray-100 border-t">
          <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
            <p className="text-xs text-center text-muted-foreground">
              Les règles fiscales évoluent suite à la réforme votée en Suisse en 2025.
              Les déductions actuelles restent applicables pendant la phase transitoire.
              Les estimations présentées sont indicatives et doivent être validées au cas par cas.
            </p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};
